import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowLeftRight,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  Pencil,
  RefreshCw,
  Send,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill } from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingError,
  AccountingLoading,
} from '@/components/admin/accounting/AccountingShell';
import InvoiceActionsMenu, {
  documentOwnerPath,
  invoiceCapabilities,
  useInvoiceWorkflow,
} from '@/components/admin/accounting/InvoiceWorkflow';
import { accountingError, formatCents, invoiceStatusMeta, shortDate } from '@/lib/accounting';
import {
  getAccountingCustomers,
  getAccountingInvoice,
  getAccountingInvoiceActivity,
  getAccountingInvoicePreview,
  getAccountingItems,
  getAccountingTaxes,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import PaymentActions from '@/components/admin/accounting/PaymentActions';
import { restoreAccountingInvoiceRevision } from '@/lib/api';
import { toast } from 'sonner';

/**
 * One invoice, and everything that has happened to it.
 *
 * Three panels, in the order the questions get asked: what state is this in and
 * what is owed; what has been done to it and by whom; and what the customer
 * actually sees. The last of those is the portal's own invoice template
 * rendered by the server from the same context that produces the PDF, in a
 * sandboxed frame — so this page cannot show one thing while the document says
 * another.
 *
 * The actions are the list's actions. They come from the same hook, so
 * "Duplicate" here and "Duplicate" there are one behaviour rather than two
 * implementations that agree for now.
 */

function Fact({ label, value, tone, hint }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
        {label}
      </p>
      <div
        className={cn(
          'mt-1 truncate text-lg font-semibold',
          tone === 'red' ? 'text-red-700' : 'text-[#062B1A]'
        )}
      >
        {value}
      </div>
      {hint && <p className="mt-0.5 truncate text-xs text-[#062B1A]/45">{hint}</p>}
    </div>
  );
}

// The invoice template is a fixed-width page — 816px of paper plus its frame —
// because it is the same document that gets printed. Dropping it into a narrower
// panel would clip it and wrap every line, so the whole page is scaled down to
// whatever width the panel has, the way a document viewer zooms to fit.
const DOCUMENT_WIDTH = 880;
const DOCUMENT_HEIGHT = 1180;

function useFitToWidth() {
  const [scale, setScale] = useState(1);
  const [node, setNode] = useState(null);

  useEffect(() => {
    if (!node) return undefined;
    const measure = () => setScale(Math.min(1, node.clientWidth / DOCUMENT_WIDTH));
    measure();
    // ResizeObserver catches the sidebar collapsing as well as the window,
    // which a resize listener alone would miss.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref: setNode, scale };
}

function PanelHeading({ children, action }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[rgba(6,43,26,0.07)] px-4 py-3">
      <h2 className="text-sm font-semibold text-[#062B1A]">{children}</h2>
      {action}
    </div>
  );
}

export default function AccountingInvoiceDetail() {
  const { ref: rawRef } = useParams();
  const ref = decodeURIComponent(rawRef || '');
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [activity, setActivity] = useState([]);
  const [preview, setPreview] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [restoring, setRestoring] = useState('');
  const documentFrame = useFitToWidth();

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const [detail, trail] = await Promise.all([
          getAccountingInvoice(ref),
          getAccountingInvoiceActivity(ref),
        ]);
        setInvoice(detail.data);
        setActivity(trail.data?.events || []);
      } catch (err) {
        setError(accountingError(err, 'This invoice could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [ref]
  );

  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  // The rendered invoice is fetched separately: it is the slowest part of the
  // page and the least urgent, and a slow render must not hold up the figures.
  useEffect(() => {
    let cancelled = false;
    setPreview('');
    setPreviewError('');
    (async () => {
      try {
        const { data } = await getAccountingInvoicePreview(ref);
        if (!cancelled) setPreview(data.html || '');
      } catch (err) {
        if (!cancelled) {
          setPreviewError(accountingError(err, 'The invoice could not be rendered.'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ref, invoice?.updated_at]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [customerList, itemList, taxList] = await Promise.all([
          getAccountingCustomers({ page_size: 100 }),
          getAccountingItems({ context: 'sell', status: 'active', page_size: 100 }),
          getAccountingTaxes(),
        ]);
        if (cancelled) return;
        setCustomers(customerList.data?.items || []);
        setItems(itemList.data?.items || []);
        setTaxes(taxList.data?.items || []);
      } catch {
        // The edit dialog degrades to empty pickers; nothing else needs these.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const workflow = useInvoiceWorkflow({
    reload: () => load({ quiet: true }),
    customers,
    items,
    taxes,
    paymentMethods: invoice?.payment_methods,
  });

  const permissions = invoice?.permissions || {};
  const able = invoice ? invoiceCapabilities(invoice, permissions) : null;
  const meta = invoiceStatusMeta(invoice?.status);
  const ownerPath = documentOwnerPath(invoice);

  const restoreRevision = async (revision) => {
    if (!window.confirm(`Restore invoice version ${revision.version}? Current contents will remain in history, and saved payments stay attached.`)) return;
    setRestoring(revision.id);
    try {
      await restoreAccountingInvoiceRevision(invoice.ref, revision.id);
      toast.success('Invoice version restored.');
      await load();
    } catch (err) { toast.error(accountingError(err, 'The revision could not be restored.')); }
    finally { setRestoring(''); }
  };

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={() => navigate('/admin/accounting/sales/invoices')}>
        <ArrowLeft className="mr-2 size-4" />
        All invoices
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={() => load({ quiet: true })}
        disabled={refreshing}
      >
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {invoice && (
        <InvoiceActionsMenu
          invoice={invoice}
          permissions={permissions}
          actions={workflow.actions}
          onDuplicated={(created) =>
            navigate(`/admin/accounting/sales/invoices/${encodeURIComponent(created.ref)}`)
          }
        />
      )}
    </>
  );

  return (
    <AccountingShell
      title={invoice ? `Invoice ${invoice.number}` : 'Invoice'}
      description={
        invoice
          ? `${invoice.origin_label} · raised for ${invoice.customer?.name}`
          : 'Loading this invoice.'
      }
      actions={actions}
    >
      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading || !invoice ? (
        <AccountingLoading label="Loading this invoice…" />
      ) : (
        <>
          {/* ── What state is this in, and what is owed ── */}
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="grid gap-5 p-5 sm:grid-cols-2 xl:grid-cols-5">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                  Status
                </p>
                <div className="mt-1.5">
                  <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                </div>
                {!['draft', 'void', 'sent'].includes(invoice.status) && <p className="mt-1.5 text-xs text-[#062B1A]/50">Document: Confirmed (Issued)</p>}
                {invoice.due_phrase && (
                  <p
                    className={cn(
                      'mt-1.5 text-xs',
                      invoice.status === 'overdue' ? 'text-red-700' : 'text-[#062B1A]/50'
                    )}
                  >
                    {invoice.due_phrase}
                  </p>
                )}
              </div>
              <Fact
                label="Customer"
                value={
                  invoice.customer?.link ? (
                    <Link to={invoice.customer.link} className="hover:underline">
                      {invoice.customer.name}
                    </Link>
                  ) : (
                    invoice.customer?.name
                  )
                }
                hint={invoice.customer?.email || invoice.customer?.source_label}
              />
              <Fact
                label="Amount due"
                value={invoice.amount_due_display}
                tone={invoice.status === 'overdue' ? 'red' : undefined}
                hint={`${invoice.total_display} invoiced`}
              />
              <Fact label="Invoice date" value={invoice.issue_date || '—'} />
              <Fact
                label="Payment due"
                value={invoice.due_date || '—'}
                hint={
                  invoice.last_sent?.sent_at
                    ? `Last sent ${shortDate(invoice.last_sent.sent_at)}`
                    : 'Not yet emailed'
                }
              />
            </CardContent>
          </Card>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
            <div className="space-y-5">
              {/* ── Manage payments ── */}
              <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
                <PanelHeading
                  action={
                    able?.payment.allowed ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => workflow.actions.payment(invoice)}
                        className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                      >
                        <Wallet className="mr-1.5 size-3.5" />
                        Record payment
                      </Button>
                    ) : null
                  }
                >
                  Payments
                </PanelHeading>
                <CardContent className="space-y-3 p-4">
                  <dl className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#062B1A]/45">
                        Invoice total
                      </dt>
                      <dd className="mt-0.5 font-semibold text-[#062B1A]">
                        {invoice.total_display}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#062B1A]/45">
                        Received
                      </dt>
                      <dd className="mt-0.5 font-semibold text-emerald-700">
                        {invoice.paid_display}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#062B1A]/45">
                        Still due
                      </dt>
                      <dd
                        className={cn(
                          'mt-0.5 font-semibold',
                          invoice.amount_due_cents > 0 ? 'text-[#062B1A]' : 'text-emerald-700'
                        )}
                      >
                        {invoice.amount_due_display}
                      </dd>
                    </div>
                  </dl>

                  {invoice.payments?.length > 0 && (
                    <ul className="divide-y divide-[rgba(6,43,26,0.07)] rounded-lg border border-[rgba(6,43,26,0.08)]">
                      {invoice.payments.map((payment) => (
                        <li key={payment.id} className="flex items-start justify-between gap-3 px-3 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm text-[#062B1A]">
                              {payment.payment_date}
                              {payment.method ? ` · ${payment.method}` : ''}
                            </p>
                            <p className="truncate text-[11px] text-[#062B1A]/45">
                              {[
                                payment.reference,
                                payment.recorded_by ? `Recorded by ${payment.recorded_by}` : '',
                              ]
                                .filter(Boolean)
                                .join(' · ') || 'On this invoice'}
                            </p>
                          </div>
                          <span className="shrink-0 font-semibold text-[#062B1A]">
                            {payment.amount_display}
                            <PaymentActions payment={payment} permissions={permissions} methods={invoice.payment_methods || []} onChanged={load} />
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Money from this customer that named no invoice, which
                      allocation applied here. It is real and it is counted; the
                      list above cannot show it because it belongs to no single
                      payment against this invoice. */}
                  {invoice.allocated_credit_cents > 0 && (
                    <p className="rounded-lg bg-[#F7F8FA] px-3 py-2 text-xs leading-relaxed text-[#062B1A]/60">
                      {formatCents(invoice.allocated_credit_cents)} of what is shown as received
                      came from payments on this customer&apos;s ledger that name no invoice.
                      It is applied to their oldest unpaid invoices first.
                    </p>
                  )}

                  {invoice.payments?.length === 0 && invoice.allocated_credit_cents === 0 && (
                    <p className="rounded-lg bg-[#F7F8FA] px-3 py-2 text-xs text-[#062B1A]/55">
                      Nothing has been received against this invoice yet.
                    </p>
                  )}

                  <p className="flex items-start gap-1.5 text-[11px] leading-snug text-[#062B1A]/45">
                    <ArrowLeftRight className="mt-0.5 size-3 shrink-0" />
                    Every payment here appears in{' '}
                    <Link to="/admin/accounting/transactions" className="underline">
                      Transactions
                    </Link>{' '}
                    on its own. It is never entered twice.
                  </p>
                </CardContent>
              </Card>

              {/* ── What has happened to it ── */}
              {invoice.revision_history?.length > 0 && (
                <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
                  <PanelHeading>Version history</PanelHeading>
                  <CardContent className="space-y-3 p-4">
                    <p className="text-xs text-[#062B1A]/55">Current version {invoice.revision_number}. Earlier contents remain available; restoring keeps saved payments and sending history.</p>
                    {[...invoice.revision_history].reverse().map((revision) => <div key={revision.id} className="flex items-center justify-between gap-3 text-sm">
                      <span>Version {revision.version} · {shortDate(revision.saved_at)} · {revision.saved_by}</span>
                      {permissions.edit_invoice && <Button type="button" variant="outline" size="sm" disabled={Boolean(restoring)} onClick={() => restoreRevision(revision)}>Restore version</Button>}
                    </div>)}
                  </CardContent>
                </Card>
              )}
              <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
                <PanelHeading>Activity</PanelHeading>
                <CardContent className="p-4">
                  {activity.length === 0 ? (
                    <p className="text-xs text-[#062B1A]/50">
                      Nothing recorded against this invoice yet.
                    </p>
                  ) : (
                    <ol className="space-y-3">
                      {activity.map((event, index) => (
                        <li key={`${event.key}-${event.at}-${index}`} className="flex gap-3">
                          <span
                            className="mt-1.5 size-1.5 shrink-0 rounded-full bg-[#D4AF37]"
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#062B1A]">{event.label}</p>
                            {event.detail && (
                              <p className="text-xs text-[#062B1A]/55">{event.detail}</p>
                            )}
                            <p className="mt-0.5 text-[11px] text-[#062B1A]/40">
                              {shortDate(event.at)}
                              {event.by ? ` · ${event.by}` : ''}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── What the customer sees ── */}
            <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
              <PanelHeading
                action={
                  <div className="flex items-center gap-1.5">
                    {able?.email.allowed && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => workflow.actions.email(invoice)}
                      >
                        <Send className="mr-1.5 size-3.5" />
                        {invoice.send_count > 0 ? 'Resend' : 'Send'}
                      </Button>
                    )}
                    {able?.pdf.allowed && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => workflow.actions.pdf(invoice)}
                      >
                        <Download className="mr-1.5 size-3.5" />
                        PDF
                      </Button>
                    )}
                  </div>
                }
              >
                The invoice
              </PanelHeading>
              <CardContent className="p-0">
                {previewError ? (
                  <p className="px-4 py-6 text-sm text-red-700">{previewError}</p>
                ) : preview ? (
                  <div
                    ref={documentFrame.ref}
                    className="overflow-hidden bg-[#5c5f66]"
                    style={{ height: DOCUMENT_HEIGHT * documentFrame.scale }}
                  >
                    {/* Sandboxed with no allowances: it renders the document
                        and can do nothing else. */}
                    <iframe
                      title={`Invoice ${invoice.number}`}
                      sandbox=""
                      srcDoc={preview}
                      className="border-0 bg-[#5c5f66]"
                      style={{
                        width: DOCUMENT_WIDTH,
                        height: DOCUMENT_HEIGHT,
                        transform: `scale(${documentFrame.scale})`,
                        transformOrigin: 'top left',
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex h-[240px] items-center justify-center text-sm text-[#062B1A]/45">
                    <Loader2 className="mr-2 size-4 animate-spin text-[#D4AF37]" />
                    Rendering the invoice…
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Secondary routes out, for the invoices this module only reads. */}
          <div className="flex flex-wrap items-center gap-2">
            {able?.edit.allowed && (
              <Button type="button" variant="outline" size="sm" onClick={() => workflow.actions.edit(invoice)}>
                <Pencil className="mr-1.5 size-3.5" />
                Edit invoice
              </Button>
            )}
            {permissions.duplicate_invoice && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  workflow.actions.duplicate(invoice, (created) =>
                    navigate(`/admin/accounting/sales/invoices/${encodeURIComponent(created.ref)}`)
                  )
                }
              >
                <Copy className="mr-1.5 size-3.5" />
                Duplicate
              </Button>
            )}
            {ownerPath && (
              <Button asChild variant="ghost" size="sm">
                <Link to={ownerPath}>
                  <ExternalLink className="mr-1.5 size-3.5" />
                  Open the {invoice.student_id ? 'student' : 'application'}
                </Link>
              </Button>
            )}
          </div>
        </>
      )}

      {workflow.dialogs}
    </AccountingShell>
  );
}
